/**
 * Realtime entity-change client for the Foundation SDK.
 *
 * Maintains a single shared WebSocket to the backend's realtime endpoint
 * (the `websocketBaseUrl` JWT claim). The socket is opened lazily on the
 * first subscriber and closed on the last unsubscribe. Server-initiated
 * drops are normal (API Gateway ~10 min idle timeout, 2 h connection TTL),
 * so the client heartbeats and reconnects with exponential backoff, always
 * re-reading the endpoint and auth token from a fresh JWT.
 *
 * Wire protocol (verified against Core):
 * - Connect: `${websocketBaseUrl}?authorizationToken=<raw JWT>` (Lambda
 *   authorizer reads the `authorizationToken` query-string param).
 * - Heartbeat: send `{"action":"ping"}`, server answers
 *   `{"action":"pong","timestamp":...}` on the $default route.
 * - Push: `{ type: "entity.created"|"entity.modified"|"entity.deleted",
 *   entityId, resource, id, __namespace, timestamp }`.
 */
import type { EntityChangeEvent } from './types'

export const PING_INTERVAL_MS = 5 * 60 * 1000
export const PONG_TIMEOUT_MS = 30 * 1000
export const BACKOFF_BASE_MS = 1000
export const BACKOFF_CAP_MS = 30 * 1000
export const DEDUP_CACHE_SIZE = 100

const WS_OPEN = 1

/** Minimal structural view of a WebSocket, so the client works with any
 *  spec-compliant implementation (browser, happy-dom, test mocks). */
interface WebSocketLike {
  readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: (() => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onclose: ((event: { code: number; reason: string }) => void) | null
  onerror: (() => void) | null
}

type WebSocketConstructor = new (url: string) => WebSocketLike

function resolveWebSocketConstructor(): WebSocketConstructor | undefined {
  const globalScope = globalThis as { WebSocket?: unknown }
  return typeof globalScope.WebSocket === 'function'
    ? (globalScope.WebSocket as WebSocketConstructor)
    : undefined
}

export interface RealtimeClientOptions {
  /** Returns a fresh JWT. Called on every (re)connect attempt — never cached,
   *  because the previous token may have expired (the 2 h TTL case). */
  getToken: () => Promise<string>
  /** Extracts the realtime endpoint from a token (the `websocketBaseUrl`
   *  claim). Returns '' when the backend has no realtime support. */
  getWebsocketBaseUrl: (token: string) => string
  /** Diagnostics sink; the client never throws from background activity. */
  warn: (message: string, context?: Record<string, unknown>) => void
}

export interface RealtimeClient {
  /** Register a listener for entity-change events. Opens the shared socket
   *  on the first subscriber; the returned function unsubscribes (idempotent)
   *  and closes the socket when the last subscriber leaves. */
  subscribe(listener: (event: EntityChangeEvent) => void): () => void
}

export function createRealtimeClient(options: RealtimeClientOptions): RealtimeClient {
  const listeners: Array<(event: EntityChangeEvent) => void> = []

  let socket: WebSocketLike | null = null
  /** Bumped to invalidate in-flight connects (e.g. unsubscribe during token fetch). */
  let connectGeneration = 0
  /** Generation of the connect currently awaiting a token, or null. */
  let activeConnectGeneration: number | null = null
  let reconnectAttempts = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let pingTimer: ReturnType<typeof setInterval> | null = null
  let pongTimer: ReturnType<typeof setTimeout> | null = null
  /** Token acquisition failed (logged out); cleared by the next subscribe. */
  let suspended = false
  /** No WebSocket global, or the backend has no websocketBaseUrl claim. */
  let permanentlyDisabled = false
  let onlineListener: (() => void) | null = null

  /** Insertion-ordered bounded cache for duplicate-delivery suppression. */
  const seenEventKeys = new Set<string>()

  function clearReconnectTimer(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function stopHeartbeat(): void {
    if (pingTimer !== null) {
      clearInterval(pingTimer)
      pingTimer = null
    }
    if (pongTimer !== null) {
      clearTimeout(pongTimer)
      pongTimer = null
    }
  }

  function startHeartbeat(ws: WebSocketLike): void {
    stopHeartbeat()
    pingTimer = setInterval(() => {
      if (ws !== socket || ws.readyState !== WS_OPEN) return
      try {
        ws.send(JSON.stringify({ action: 'ping' }))
      } catch (err) {
        options.warn('Realtime heartbeat send failed', { error: String(err) })
        return
      }
      if (pongTimer === null) {
        pongTimer = setTimeout(() => {
          pongTimer = null
          if (ws !== socket) return
          options.warn('Realtime heartbeat timed out; reconnecting')
          // Closing triggers onclose, which drives the reconnect.
          try { ws.close(4000, 'heartbeat timeout') } catch { /* already closed */ }
        }, PONG_TIMEOUT_MS)
      }
    }, PING_INTERVAL_MS)
  }

  function backoffDelay(attempt: number): number {
    const capped = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt)
    // Jitter: uniform in [capped/2, capped] to avoid thundering herds.
    return capped / 2 + Math.random() * (capped / 2)
  }

  function scheduleReconnect(): void {
    if (reconnectTimer !== null || permanentlyDisabled || suspended || listeners.length === 0) return
    const delay = backoffDelay(reconnectAttempts)
    reconnectAttempts += 1
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void connect()
    }, delay)
  }

  function dispatch(event: EntityChangeEvent): void {
    // Snapshot: listeners may (un)subscribe from within a callback.
    for (const listener of [...listeners]) {
      try {
        listener(event)
      } catch (err) {
        options.warn('Realtime listener threw', { error: String(err) })
      }
    }
  }

  function rememberEventKey(key: string): boolean {
    if (seenEventKeys.has(key)) return false
    seenEventKeys.add(key)
    if (seenEventKeys.size > DEDUP_CACHE_SIZE) {
      const oldest = seenEventKeys.values().next().value
      if (oldest !== undefined) seenEventKeys.delete(oldest)
    }
    return true
  }

  function handleMessage(data: unknown): void {
    if (typeof data !== 'string') return
    let parsed: unknown
    try {
      parsed = JSON.parse(data)
    } catch {
      return // malformed frame — ignore
    }
    if (parsed === null || typeof parsed !== 'object') return
    const message = parsed as Record<string, unknown>

    if (message.action === 'pong') {
      if (pongTimer !== null) {
        clearTimeout(pongTimer)
        pongTimer = null
      }
      return
    }
    // Other action responses (echo, error) are not entity events.
    if (typeof message.action === 'string') return

    const wireType = message.type
    if (typeof wireType !== 'string' || !wireType.startsWith('entity.')) return

    // DynamoDB streams + Lambda retries can double-deliver.
    const key = `${wireType}:${String(message.entityId ?? '')}:${String(message.id ?? '')}:${String(message.timestamp ?? '')}`
    if (!rememberEventKey(key)) return

    const event: EntityChangeEvent = { changeType: wireType.slice('entity.'.length) }
    if (typeof message.entityId === 'string') event.entityId = message.entityId
    if (typeof message.resource === 'string') event.resource = message.resource
    if (typeof message.id === 'string') event.id = message.id
    if (typeof message.__namespace === 'string') event.namespace = message.__namespace
    if (typeof message.timestamp === 'number') event.timestamp = message.timestamp
    dispatch(event)
  }

  function handleClose(): void {
    stopHeartbeat()
    socket = null
    if (listeners.length === 0) return // client-initiated close — stay down
    scheduleReconnect()
  }

  async function connect(): Promise<void> {
    if (permanentlyDisabled || suspended || socket !== null || listeners.length === 0) return
    if (activeConnectGeneration === connectGeneration) return // already connecting
    const generation = connectGeneration
    activeConnectGeneration = generation
    const finish = (): void => {
      if (activeConnectGeneration === generation) activeConnectGeneration = null
    }

    let token: string
    try {
      token = await options.getToken()
    } catch (err) {
      finish()
      if (generation !== connectGeneration) return // unsubscribed meanwhile
      suspended = true
      options.warn(
        'Realtime paused: could not obtain an auth token (are you logged out?). ' +
        'It will retry on the next subscribe.',
        { error: String(err) }
      )
      return
    }
    finish()
    if (generation !== connectGeneration || socket !== null || listeners.length === 0) return

    const WebSocketCtor = resolveWebSocketConstructor()
    if (!WebSocketCtor) {
      permanentlyDisabled = true
      options.warn('Realtime disabled: no WebSocket implementation in this environment (SSR/Node). Listeners stay registered but will never fire.')
      return
    }

    let baseUrl = ''
    try {
      baseUrl = options.getWebsocketBaseUrl(token)
    } catch {
      baseUrl = ''
    }
    if (!baseUrl) {
      permanentlyDisabled = true
      options.warn('Realtime disabled: the backend token has no websocketBaseUrl claim (realtime not deployed). Listeners stay registered but will never fire.')
      return
    }

    let ws: WebSocketLike
    try {
      ws = new WebSocketCtor(`${baseUrl}?authorizationToken=${encodeURIComponent(token)}`)
    } catch (err) {
      options.warn('Realtime connect failed', { error: String(err) })
      scheduleReconnect()
      return
    }
    socket = ws

    ws.onopen = () => {
      if (ws !== socket) return
      reconnectAttempts = 0
      startHeartbeat(ws)
    }
    ws.onmessage = (event) => {
      if (ws !== socket) return
      handleMessage(event.data)
    }
    ws.onerror = () => {
      // A close event always follows an error; reconnection is driven there.
    }
    ws.onclose = () => {
      if (ws !== socket) return
      handleClose()
    }
  }

  function handleOnline(): void {
    // Network is back — short-circuit any pending backoff.
    if (reconnectTimer === null) return
    clearReconnectTimer()
    reconnectAttempts = 0
    void connect()
  }

  function addOnlineListener(): void {
    if (onlineListener !== null) return
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
    onlineListener = () => handleOnline()
    window.addEventListener('online', onlineListener)
  }

  function removeOnlineListener(): void {
    if (onlineListener === null) return
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('online', onlineListener)
    }
    onlineListener = null
  }

  function deactivate(): void {
    connectGeneration += 1 // invalidate any in-flight connect
    clearReconnectTimer()
    reconnectAttempts = 0
    stopHeartbeat()
    removeOnlineListener()
    const ws = socket
    socket = null // handlers of this socket become no-ops (guarded by ws !== socket)
    if (ws !== null) {
      try {
        ws.close(1000, 'no subscribers')
      } catch { /* already closed */ }
    }
  }

  return {
    subscribe(listener) {
      listeners.push(listener)
      if (listeners.length === 1) addOnlineListener()
      suspended = false // a fresh subscribe restarts after token failures
      void connect()

      let removed = false
      return () => {
        if (removed) return // double-unsubscribe is a no-op
        removed = true
        const index = listeners.indexOf(listener)
        if (index > -1) listeners.splice(index, 1)
        if (listeners.length === 0) deactivate()
      }
    }
  }
}
